// src/admin/pages/CategoryConfig.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./CategoryConfig.css";
import { CONFIG } from "../../config";

/** ---- Config ---- */
const UPLOAD_URL = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;

/* ---------- Defaults ---------- */
const blankGroup = (name = "") => ({
  name,
  description: "",
  displayTitle: "",
  displayDescription: "",
  useSectionStyleConfig: false,
  applyCardStyleToAllCards: false,
  titleColor: "",
  descriptionColor: "",
  titleFontSize: "",
  descriptionFontSize: "",
  cardTitleColor: "",
  cardDescriptionColor: "",
  cardTitleFontSize: "",
  cardDescriptionFontSize: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  specialCategory: false,
  categoryGrid: false,
  latestCategory: false,
  updatesCategory: false,
  items: [],
});

const blankItem = (id = 0, label = "") => ({
  id,
  label,
  description: "",
  displayTitle: "",
  displayDescription: "",
  useSectionStyleConfig: false,
  titleColor: "",
  descriptionColor: "",
  titleFontSize: "",
  descriptionFontSize: "",
  metaTitleColor: "",
  metaDescriptionColor: "",
  metaTitleFontSize: "",
  metaDescriptionFontSize: "",
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  specialCategory: false,
  categoryGrid: false,
  latestCategory: false,
  updatesCategory: false,
  mobileCols: 2,   // ✅ default: 2 cards per row on mobile
  desktopCols: 4,  // ✅ default: 4 cards per row on desktop
  images: [],      // [{ filename, cover, metaTitle, metaDescription, metaKeywords }]
});

const parseOptionalNumber = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const toColorInputValue = (v, fallback = "#111111") =>
  /^#([0-9a-f]{3}){1,2}$/i.test(String(v || "").trim())
    ? String(v).trim()
    : fallback;

export default function CategoryConfig() {
  const [dropdowns, setDropdowns] = useState([]);
  const [groupIndex, setGroupIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ---------- Load from Firebase ---------- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await get(ref(db, "navconfig/dropdowns"));
        if (!snap.exists()) {
          setDropdowns([]);
        } else {
          const arr = Array.isArray(snap.val()) ? snap.val() : [];
          const normalized = arr.map((g) => ({
            name: g?.name || "",
            description: g?.description || "",
            displayTitle: g?.displayTitle || "",
            displayDescription: g?.displayDescription || "",
            useSectionStyleConfig: !!g?.useSectionStyleConfig,
            applyCardStyleToAllCards: !!g?.applyCardStyleToAllCards,
            titleColor: g?.titleColor || "",
            descriptionColor: g?.descriptionColor || "",
            titleFontSize: parseOptionalNumber(g?.titleFontSize),
            descriptionFontSize: parseOptionalNumber(g?.descriptionFontSize),
            cardTitleColor: g?.cardTitleColor || "",
            cardDescriptionColor: g?.cardDescriptionColor || "",
            cardTitleFontSize: parseOptionalNumber(g?.cardTitleFontSize),
            cardDescriptionFontSize: parseOptionalNumber(
              g?.cardDescriptionFontSize
            ),
            metaTitle: g?.metaTitle || "",
            metaDescription: g?.metaDescription || "",
            metaKeywords: g?.metaKeywords || "",
            specialCategory: !!g?.specialCategory,
            categoryGrid: !!g?.categoryGrid,
            latestCategory: !!g?.latestCategory,
            updatesCategory: !!g?.updatesCategory,
            items: Array.isArray(g?.items)
              ? g.items.map((it) => ({
                  id: Number(it?.id ?? 0),
                  label: it?.label || "",
                  description: it?.description || "",
                  displayTitle: it?.displayTitle || "",
                  displayDescription: it?.displayDescription || "",
                  useSectionStyleConfig: !!it?.useSectionStyleConfig,
                  titleColor: it?.titleColor || "",
                  descriptionColor: it?.descriptionColor || "",
                  titleFontSize: parseOptionalNumber(it?.titleFontSize),
                  descriptionFontSize: parseOptionalNumber(
                    it?.descriptionFontSize
                  ),
                  metaTitleColor: it?.metaTitleColor || "",
                  metaDescriptionColor: it?.metaDescriptionColor || "",
                  metaTitleFontSize: parseOptionalNumber(it?.metaTitleFontSize),
                  metaDescriptionFontSize: parseOptionalNumber(
                    it?.metaDescriptionFontSize
                  ),
                  metaTitle: it?.metaTitle || "",
                  metaDescription: it?.metaDescription || "",
                  metaKeywords: it?.metaKeywords || "",
                  specialCategory: !!it?.specialCategory,
                  categoryGrid: !!it?.categoryGrid,
                  latestCategory: !!it?.latestCategory,
                  updatesCategory: !!it?.updatesCategory,
                  mobileCols: Number(it?.mobileCols ?? 2),
                  desktopCols: Number(it?.desktopCols ?? 4),
                  images: Array.isArray(it?.images)
                    ? it.images.map((img) =>
                        typeof img === "string"
                          ? {
                              filename: img,
                              cover: false,
                              metaTitle: "",
                              metaDescription: "",
                              metaKeywords: "",
                            }
                          : {
                              filename: img.filename || img.name || "",
                              cover: !!img.cover,
                              metaTitle: img.metaTitle || "",
                              metaDescription: img.metaDescription || "",
                              metaKeywords: img.metaKeywords || "",
                            }
                      )
                    : [],
                }))
              : [],
          }));
          setDropdowns(normalized);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load dropdowns");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => setItemIndex(0), [groupIndex]);

  const selectedGroup = useMemo(
    () => dropdowns[groupIndex] || null,
    [dropdowns, groupIndex]
  );
  const selectedItem = useMemo(
    () => selectedGroup?.items?.[itemIndex] || null,
    [selectedGroup, itemIndex]
  );

  /* ---------- Save helper ---------- */
  const saveAll = async (next = dropdowns) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await set(ref(db, "navconfig/dropdowns"), next);
      setDropdowns(next);
      setSuccess("Saved successfully ✔");
    } catch (e) {
      console.error(e);
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Group CRUD ---------- */
  const addGroup = () => {
    const next = [...dropdowns, blankGroup(`Group ${dropdowns.length + 1}`)];
    setDropdowns(next);
    setGroupIndex(next.length - 1);
    setItemIndex(0);
  };

  const removeGroup = (idx) => {
    if (!window.confirm("Delete this category?")) return;
    const next = dropdowns.filter((_, i) => i !== idx);
    setDropdowns(next);
    setGroupIndex(Math.max(0, next.length - 1));
    setItemIndex(0);
  };

  const updateGroupField = (idx, field, val) =>
    setDropdowns((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, [field]: val } : g))
    );

  const toggleGroupFlag = (idx, flag) =>
    setDropdowns((prev) =>
      prev.map((g, i) =>
        i === idx ? { ...g, [flag]: !g[flag] } : g
      )
    );

  /* ---------- Item CRUD ---------- */
  const addItem = (gIdx) => {
    setDropdowns((prev) =>
      prev.map((g, i) =>
        i === gIdx
          ? {
              ...g,
              items: [
                ...g.items,
                blankItem((g.items?.length || 0) + 1, ""),
              ],
            }
          : g
      )
    );
    setItemIndex(selectedGroup?.items?.length || 0);
  };

  const removeItem = (gIdx, itIdx) => {
    if (!window.confirm("Delete this subcategory?")) return;
    setDropdowns((prev) =>
      prev.map((g, i) =>
        i === gIdx
          ? { ...g, items: g.items.filter((_, j) => j !== itIdx) }
          : g
      )
    );
    setItemIndex(0);
  };

  const updateItemField = (gIdx, itIdx, field, value) =>
    setDropdowns((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) =>
                ii === itIdx ? { ...it, [field]: value } : it
              ),
            }
      )
    );

  const toggleItemFlag = (gIdx, itIdx, flag) =>
    setDropdowns((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) =>
                ii === itIdx ? { ...it, [flag]: !it[flag] } : it
              ),
            }
      )
    );

  /* ---------- Image helpers (incl. image meta) ---------- */
  const uploadImageForSelected = async (file) => {
    if (!file || !selectedItem) return;
    try {
      setUploading(true);
      setError("");
      setSuccess("");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      const filename = data?.filename;
      if (!filename) throw new Error("No filename returned");

      const next = dropdowns.map((g, gi) =>
        gi !== groupIndex
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) =>
                ii !== itemIndex
                  ? it
                  : {
                      ...it,
                      images: [
                        ...(it.images || []),
                        {
                          filename,
                          cover: (it.images || []).length === 0,
                          metaTitle: "",
                          metaDescription: "",
                          metaKeywords: "",
                        },
                      ],
                    }
              ),
            }
      );
      await saveAll(next);
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (filename) => {
    if (!filename || !selectedItem) return;
    if (!window.confirm("Delete this image permanently?")) return;
    try {
      const res = await fetch(
        `${IMAGES_BASE}/${encodeURIComponent(filename)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);

      const next = dropdowns.map((g, gi) =>
        gi !== groupIndex
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) =>
                ii !== itemIndex
                  ? it
                  : {
                      ...it,
                      images: (it.images || []).filter(
                        (im) => im.filename !== filename
                      ),
                    }
              ),
            }
      );
      await saveAll(next);
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  const toggleCover = async (filename) => {
    const isCover = !!selectedItem?.images?.find(
      (im) => im.filename === filename && im.cover
    );
    const next = dropdowns.map((g, gi) =>
      gi !== groupIndex
        ? g
        : {
            ...g,
            items: g.items.map((it, ii) =>
              ii !== itemIndex
                ? it
                : {
                    ...it,
                    images: (it.images || []).map((im) =>
                      im.filename === filename
                        ? { ...im, cover: !isCover }
                        : { ...im, cover: isCover ? im.cover : false }
                    ),
                  }
            ),
          }
    );
    await saveAll(next);
  };

  const updateImageMeta = (gIdx, iIdx, filename, field, value) => {
    setDropdowns((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : {
              ...g,
              items: g.items.map((it, ii) =>
                ii !== iIdx
                  ? it
                  : {
                      ...it,
                      images: (it.images || []).map((im) =>
                        im.filename === filename
                          ? { ...im, [field]: value }
                          : im
                      ),
                    }
              ),
            }
      )
    );
  };

  /* ---------- Render ---------- */
  if (loading) return <div className="ccfg-loading">Loading…</div>;

  return (
    <section className="ccfg-wrap">
      <h2 className="ccfg-title">Category Configuration</h2>
      {success && <p className="ccfg-success">{success}</p>}
      {error && <p className="ccfg-error">{error}</p>}

      {/* Selectors */}
      <div className="ccfg-card">
        <div className="ccfg-row">
          <div className="ccfg-col" style={{ maxWidth: 380 }}>
            <label className="ccfg-label">Category</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={groupIndex}
                onChange={(e) => setGroupIndex(Number(e.target.value))}
              >
                {dropdowns.map((g, i) => (
                  <option key={i} value={i}>
                    {g.displayTitle || g.name || `Category ${i + 1}`}
                  </option>
                ))}
              </select>
              <button className="ccfg-btn" onClick={addGroup}>
                + Category
              </button>
              <button
                className="ccfg-btn danger outline"
                onClick={() => removeGroup(groupIndex)}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="ccfg-col" style={{ maxWidth: 460 }}>
            <label className="ccfg-label">Subcategory</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={itemIndex}
                onChange={(e) => setItemIndex(Number(e.target.value))}
                disabled={!selectedGroup?.items?.length}
              >
                {(selectedGroup?.items || []).map((it, i) => (
                  <option key={i} value={i}>
                    {`${it.id}. ${
                      it.displayTitle || it.label || "(untitled)"
                    }`}
                  </option>
                ))}
              </select>
              <button
                className="ccfg-btn"
                onClick={() => addItem(groupIndex)}
              >
                + Subcategory
              </button>
              <button
                className="ccfg-btn danger outline"
                onClick={() => removeItem(groupIndex, itemIndex)}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="ccfg-col" style={{ alignItems: "flex-end" }}>
            <button
              className="ccfg-btn primary"
              onClick={() => saveAll(dropdowns)}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save All"}
            </button>
          </div>
        </div>
      </div>

      {/* Category Details + META */}
      {selectedGroup && (
        <div className="ccfg-card">
          <h3 className="ccfg-subtitle">Category Details</h3>

          <div className="ccfg-row">
            <div className="ccfg-col">
              <label className="ccfg-label">Internal Name</label>
              <input
                value={selectedGroup.name}
                onChange={(e) =>
                  updateGroupField(groupIndex, "name", e.target.value)
                }
                placeholder="e.g., Design Ideas"
              />
            </div>
            <div className="ccfg-col">
              <label className="ccfg-label">Display Title</label>
              <input
                value={selectedGroup.displayTitle}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "displayTitle",
                    e.target.value
                  )
                }
                placeholder="Shown on site (H1)"
              />
            </div>
          </div>

          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Display Description</label>
              <textarea
                rows={3}
                value={selectedGroup.displayDescription}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "displayDescription",
                    e.target.value
                  )
                }
                placeholder="Short intro for the category page"
              />
            </div>
          </div>

          <div className="ccfg-row column">
            <h4 className="ccfg-mini-title">Category Page Text Style</h4>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.useSectionStyleConfig}
                onChange={() =>
                  updateGroupField(
                    groupIndex,
                    "useSectionStyleConfig",
                    !selectedGroup.useSectionStyleConfig
                  )
                }
              />
              <span>
                Use this section style config (otherwise Homepage config is used)
              </span>
            </label>
            {!selectedGroup.useSectionStyleConfig && (
              <p className="ccfg-note">Currently using Homepage style config.</p>
            )}
            <div className="ccfg-style-grid">
              <div className="ccfg-col">
                <label className="ccfg-label">Heading Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedGroup.titleColor)}
                    onChange={(e) =>
                      updateGroupField(groupIndex, "titleColor", e.target.value)
                    }
                  />
                  <input
                    value={selectedGroup.titleColor}
                    onChange={(e) =>
                      updateGroupField(groupIndex, "titleColor", e.target.value)
                    }
                    placeholder="#111111"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Heading Font Size (px)</label>
                <input
                  type="number"
                  min={10}
                  value={selectedGroup.titleFontSize}
                  onChange={(e) =>
                    updateGroupField(
                      groupIndex,
                      "titleFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Description Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedGroup.descriptionColor)}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "descriptionColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedGroup.descriptionColor}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "descriptionColor",
                        e.target.value
                      )
                    }
                    placeholder="#6b7280"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">
                  Description Font Size (px)
                </label>
                <input
                  type="number"
                  min={10}
                  value={selectedGroup.descriptionFontSize}
                  onChange={(e) =>
                    updateGroupField(
                      groupIndex,
                      "descriptionFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
            </div>
          </div>

          <div className="ccfg-row column">
            <h4 className="ccfg-mini-title">Subcategory Card Text Style</h4>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.applyCardStyleToAllCards}
                onChange={() =>
                  updateGroupField(
                    groupIndex,
                    "applyCardStyleToAllCards",
                    !selectedGroup.applyCardStyleToAllCards
                  )
                }
              />
              <span>Apply this card style to all subcategory cards</span>
            </label>
            {!selectedGroup.applyCardStyleToAllCards && (
              <p className="ccfg-note">
                When off, cards use section/item style config fallback.
              </p>
            )}
            <div className="ccfg-style-grid">
              <div className="ccfg-col">
                <label className="ccfg-label">Card Title Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedGroup.cardTitleColor)}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "cardTitleColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedGroup.cardTitleColor}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "cardTitleColor",
                        e.target.value
                      )
                    }
                    placeholder="#111111"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Card Title Font Size (px)</label>
                <input
                  type="number"
                  min={10}
                  value={selectedGroup.cardTitleFontSize}
                  onChange={(e) =>
                    updateGroupField(
                      groupIndex,
                      "cardTitleFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Card Description Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedGroup.cardDescriptionColor)}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "cardDescriptionColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedGroup.cardDescriptionColor}
                    onChange={(e) =>
                      updateGroupField(
                        groupIndex,
                        "cardDescriptionColor",
                        e.target.value
                      )
                    }
                    placeholder="#6b7280"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">
                  Card Description Font Size (px)
                </label>
                <input
                  type="number"
                  min={10}
                  value={selectedGroup.cardDescriptionFontSize}
                  onChange={(e) =>
                    updateGroupField(
                      groupIndex,
                      "cardDescriptionFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
            </div>
          </div>

          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Internal Description</label>
              <textarea
                rows={2}
                value={selectedGroup.description}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "description",
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* META — Category */}
          <div className="ccfg-meta-grid">
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Title</label>
              <input
                value={selectedGroup.metaTitle}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "metaTitle",
                    e.target.value
                  )
                }
                placeholder="SEO title"
              />
            </div>
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Keywords</label>
              <input
                value={selectedGroup.metaKeywords}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "metaKeywords",
                    e.target.value
                  )
                }
                placeholder="comma, separated, keywords"
              />
            </div>
          </div>
          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Description</label>
              <textarea
                rows={3}
                value={selectedGroup.metaDescription}
                onChange={(e) =>
                  updateGroupField(
                    groupIndex,
                    "metaDescription",
                    e.target.value
                  )
                }
                placeholder="SEO description"
              />
            </div>
          </div>

          <div className="ccfg-row">
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.specialCategory}
                onChange={() =>
                  toggleGroupFlag(groupIndex, "specialCategory")
                }
              />
              <span>Special Category</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.categoryGrid}
                onChange={() =>
                  toggleGroupFlag(groupIndex, "categoryGrid")
                }
              />
              <span>Category Grid</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.latestCategory}
                onChange={() =>
                  toggleGroupFlag(groupIndex, "latestCategory")
                }
              />
              <span>Latest Category</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedGroup.updatesCategory}
                onChange={() =>
                  toggleGroupFlag(groupIndex, "updatesCategory")
                }
              />
              <span>Updates Category</span>
            </label>
          </div>
        </div>
      )}

      {/* Subcategory Details + META */}
      {selectedItem && (
        <div className="ccfg-card">
          <h3 className="ccfg-subtitle">Subcategory Details</h3>

          <div className="ccfg-row">
            <div className="ccfg-col" style={{ maxWidth: 120 }}>
              <label className="ccfg-label">ID</label>
              <input
                type="number"
                value={selectedItem.id}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "id",
                    Number(e.target.value || 0)
                  )
                }
              />
            </div>
            <div className="ccfg-col">
              <label className="ccfg-label">Internal Label</label>
              <input
                value={selectedItem.label}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "label",
                    e.target.value
                  )
                }
                placeholder="e.g., Living Room"
              />
            </div>
            <div className="ccfg-col">
              <label className="ccfg-label">Display Title</label>
              <input
                value={selectedItem.displayTitle}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "displayTitle",
                    e.target.value
                  )
                }
                placeholder="Shown on site (H2)"
              />
            </div>
          </div>

          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Display Description</label>
              <textarea
                rows={3}
                value={selectedItem.displayDescription}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "displayDescription",
                    e.target.value
                  )
                }
                placeholder="Short copy for subcategory section"
              />
            </div>
          </div>

          <div className="ccfg-row column">
            <h4 className="ccfg-mini-title">Subcategory Page Text Style</h4>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedItem.useSectionStyleConfig}
                onChange={() =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "useSectionStyleConfig",
                    !selectedItem.useSectionStyleConfig
                  )
                }
              />
              <span>
                Use this section style config (otherwise Homepage config is used)
              </span>
            </label>
            {!selectedItem.useSectionStyleConfig && (
              <p className="ccfg-note">Currently using Homepage style config.</p>
            )}
            <div className="ccfg-style-grid">
              <div className="ccfg-col">
                <label className="ccfg-label">Heading Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedItem.titleColor)}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "titleColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedItem.titleColor}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "titleColor",
                        e.target.value
                      )
                    }
                    placeholder="#111111"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Heading Font Size (px)</label>
                <input
                  type="number"
                  min={10}
                  value={selectedItem.titleFontSize}
                  onChange={(e) =>
                    updateItemField(
                      groupIndex,
                      itemIndex,
                      "titleFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Description Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedItem.descriptionColor)}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "descriptionColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedItem.descriptionColor}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "descriptionColor",
                        e.target.value
                      )
                    }
                    placeholder="#6b7280"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">
                  Description Font Size (px)
                </label>
                <input
                  type="number"
                  min={10}
                  value={selectedItem.descriptionFontSize}
                  onChange={(e) =>
                    updateItemField(
                      groupIndex,
                      itemIndex,
                      "descriptionFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
            </div>
          </div>

          <div className="ccfg-row column">
            <h4 className="ccfg-mini-title">Image Meta Text Style</h4>
            <div className="ccfg-style-grid">
              <div className="ccfg-col">
                <label className="ccfg-label">Meta Title Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedItem.metaTitleColor)}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "metaTitleColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedItem.metaTitleColor}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "metaTitleColor",
                        e.target.value
                      )
                    }
                    placeholder="#111111"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Meta Title Font Size (px)</label>
                <input
                  type="number"
                  min={10}
                  value={selectedItem.metaTitleFontSize}
                  onChange={(e) =>
                    updateItemField(
                      groupIndex,
                      itemIndex,
                      "metaTitleFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">Meta Description Color</label>
                <div className="ccfg-color-input">
                  <input
                    type="color"
                    value={toColorInputValue(selectedItem.metaDescriptionColor)}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "metaDescriptionColor",
                        e.target.value
                      )
                    }
                  />
                  <input
                    value={selectedItem.metaDescriptionColor}
                    onChange={(e) =>
                      updateItemField(
                        groupIndex,
                        itemIndex,
                        "metaDescriptionColor",
                        e.target.value
                      )
                    }
                    placeholder="#6b7280"
                  />
                </div>
              </div>
              <div className="ccfg-col">
                <label className="ccfg-label">
                  Meta Description Font Size (px)
                </label>
                <input
                  type="number"
                  min={10}
                  value={selectedItem.metaDescriptionFontSize}
                  onChange={(e) =>
                    updateItemField(
                      groupIndex,
                      itemIndex,
                      "metaDescriptionFontSize",
                      parseOptionalNumber(e.target.value)
                    )
                  }
                  placeholder="Auto"
                />
              </div>
            </div>
          </div>

          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Internal Description</label>
              <textarea
                rows={2}
                value={selectedItem.description}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "description",
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          {/* ✅ Cards per row settings */}
          <div className="ccfg-row">
            <div className="ccfg-col" style={{ maxWidth: 160 }}>
              <label className="ccfg-label">Mobile cards / row</label>
              <input
                type="number"
                min={1}
                max={6}
                value={selectedItem.mobileCols ?? 2}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "mobileCols",
                    Number(e.target.value || 1)
                  )
                }
              />
            </div>
            <div className="ccfg-col" style={{ maxWidth: 160 }}>
              <label className="ccfg-label">Desktop cards / row</label>
              <input
                type="number"
                min={1}
                max={8}
                value={selectedItem.desktopCols ?? 4}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "desktopCols",
                    Number(e.target.value || 1)
                  )
                }
              />
            </div>
          </div>

          {/* META — Subcategory */}
          <div className="ccfg-meta-grid">
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Title</label>
              <input
                value={selectedItem.metaTitle}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "metaTitle",
                    e.target.value
                  )
                }
                placeholder="SEO title"
              />
            </div>
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Keywords</label>
              <input
                value={selectedItem.metaKeywords}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "metaKeywords",
                    e.target.value
                  )
                }
                placeholder="comma, separated, keywords"
              />
            </div>
          </div>
          <div className="ccfg-row column">
            <div className="ccfg-col">
              <label className="ccfg-label">Meta Description</label>
              <textarea
                rows={3}
                value={selectedItem.metaDescription}
                onChange={(e) =>
                  updateItemField(
                    groupIndex,
                    itemIndex,
                    "metaDescription",
                    e.target.value
                  )
                }
                placeholder="SEO description"
              />
            </div>
          </div>

          {/* Flags for subcategory */}
          <div className="ccfg-row">
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedItem.specialCategory}
                onChange={() =>
                  toggleItemFlag(
                    groupIndex,
                    itemIndex,
                    "specialCategory"
                  )
                }
              />
              <span>Special Category</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedItem.categoryGrid}
                onChange={() =>
                  toggleItemFlag(
                    groupIndex,
                    itemIndex,
                    "categoryGrid"
                  )
                }
              />
              <span>Category Grid</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedItem.latestCategory}
                onChange={() =>
                  toggleItemFlag(
                    groupIndex,
                    itemIndex,
                    "latestCategory"
                  )
                }
              />
              <span>Latest Category</span>
            </label>
            <label className="ccfg-flag">
              <input
                type="checkbox"
                checked={!!selectedItem.updatesCategory}
                onChange={() =>
                  toggleItemFlag(
                    groupIndex,
                    itemIndex,
                    "updatesCategory"
                  )
                }
              />
              <span>Updates Category</span>
            </label>
          </div>

          {/* Images + Image META */}
          <div className="ccfg-gallery">
            <div className="ccfg-row">
              <div className="ccfg-col">
                <label className="ccfg-label">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) =>
                    uploadImageForSelected(e.target.files?.[0])
                  }
                />
                {uploading && (
                  <p className="ccfg-note">Uploading…</p>
                )}
                <p className="ccfg-hint">
                  Served from{" "}
                  <code>{IMAGES_BASE}/&lt;filename&gt;</code>
                </p>
              </div>
            </div>

            <div
              className="ccfg-gallery-list"
              style={{
                "--ccfg-mobile-cols": selectedItem.mobileCols ?? 2,
                "--ccfg-desktop-cols": selectedItem.desktopCols ?? 4,
              }}
            >
              {(selectedItem.images || []).length === 0 && (
                <p className="ccfg-muted">No images yet.</p>
              )}

              {(selectedItem.images || []).map((img) => (
                <div className="ccfg-thumb" key={img.filename}>
                  <img
                    src={`${IMAGES_BASE}/${encodeURIComponent(
                      img.filename
                    )}`}
                    alt={img.metaTitle || img.filename}
                  />

                  <button
                    className="delete-btn"
                    title="Delete image"
                    onClick={() => deleteImage(img.filename)}
                  >
                    ×
                  </button>

                  <div className="ccfg-thumb-body">
                    <div className="ccfg-row">
                      <button
                        className={`ccfg-btn cover-btn ${
                          img.cover ? "is-cover" : ""
                        }`}
                        onClick={() =>
                          toggleCover(img.filename)
                        }
                        title={
                          img.cover
                            ? "Unset cover"
                            : "Make cover"
                        }
                      >
                        {img.cover
                          ? "Cover ✓ (click to unset)"
                          : "Make Cover"}
                      </button>
                    </div>

                    {/* Image META fields */}
                    <div className="ccfg-row column">
                      <label className="ccfg-label">
                        Image Meta Title
                      </label>
                      <input
                        value={img.metaTitle || ""}
                        onChange={(e) =>
                          updateImageMeta(
                            groupIndex,
                            itemIndex,
                            img.filename,
                            "metaTitle",
                            e.target.value
                          )
                        }
                        placeholder="Image SEO title / alt"
                      />
                    </div>
                    <div className="ccfg-row column">
                      <label className="ccfg-label">
                        Image Meta Keywords
                      </label>
                      <input
                        value={img.metaKeywords || ""}
                        onChange={(e) =>
                          updateImageMeta(
                            groupIndex,
                            itemIndex,
                            img.filename,
                            "metaKeywords",
                            e.target.value
                          )
                        }
                        placeholder="comma, separated, keywords"
                      />
                    </div>
                    <div className="ccfg-row column">
                      <label className="ccfg-label">
                        Image Meta Description
                      </label>
                      <textarea
                        rows={2}
                        value={img.metaDescription || ""}
                        onChange={(e) =>
                          updateImageMeta(
                            groupIndex,
                            itemIndex,
                            img.filename,
                            "metaDescription",
                            e.target.value
                          )
                        }
                        placeholder="Short description for SEO"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="ccfg-actions">
        <button
          className="ccfg-btn primary"
          onClick={() => saveAll(dropdowns)}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save to Firebase"}
        </button>
      </div>
    </section>
  );
}
